// src/pages/pengamatan/destrak/Destrak.jsx
import React, { useState, useEffect } from "react";
import {
    Box, Card, CardContent,
    CircularProgress, Alert, Snackbar,
    Table, TableContainer
} from "@mui/material";
import { useOutletContext } from "react-router-dom";
import useAuthCheck from "../../../hooks/useAuthCheck";

import CustomBreadcrumbs from '../../../components/universal-features/breadcrumbs';
import DestrakTableHeader from './DestrakTableHeader';
import DestrakTableBody from './DestrakTableBody';
import DestrakToolbar from './DestrakToolbar';
import useDestrakData from './hooks/useDestrakData';
import './css/Destrak.css';

const Destrak = () => {
    const { user, loading: authLoading, userProfile } = useAuthCheck();
    const { breadcrumbs } = useOutletContext();

    // Gunakan custom hook untuk mendapatkan data
    const {
        orders,
        loadingOrders,
        snackbar,
        setSnackbar,
        userJabatan,
        userRoleNames,
        updateStatusPengamatan
    } = useDestrakData(user, userProfile);
    
    const [filteredOrders, setFilteredOrders] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    // Fungsi untuk mencari data berdasarkan lokasi
    const handleSearch = (term) => {
        setSearchTerm(term);
        if (term === "") {
            setFilteredOrders(orders);
        } else {
            const filtered = orders.filter(order => 
                order.lokasi.toLowerCase().includes(term.toLowerCase())
            );
            setFilteredOrders(filtered);
        }
    };

    // Fungsi untuk mereset filter pencarian
    const handleResetFilter = () => {
        setSearchTerm("");
        setFilteredOrders(orders);
    };

    // Update filteredOrders ketika orders berubah
    useEffect(() => {
        setFilteredOrders(orders);
    }, [orders]);

    const handleCloseSnackbar = (event, reason) => {
        if (reason === "clickaway") {
            return;
        }
        setSnackbar({ ...snackbar, open: false });
    };

    const handleSelectAll = (event) => {
        if (event.target.checked) {
            setSelectedItems(filteredOrders.map(order => order.id));
        } else {
            setSelectedItems([]);
        }
    };

    const handleSelectItem = (id) => {
        if (selectedItems.includes(id)) {
            setSelectedItems(selectedItems.filter(itemId => itemId !== id));
        } else {
            setSelectedItems([...selectedItems, id]);
        }
    };

    const handleStatusUpdate = (snackbarData) => {
        setSnackbar(snackbarData);
    };

    if (authLoading || loadingOrders) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!user) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">
                    Anda harus login untuk mengakses halaman ini.
                </Alert>
            </Box>
        );
    }

    // Fungsi untuk reload halaman
    const handleReload = () => {
        // Gunakan setTimeout untuk memberikan waktu snackbar ditampilkan sebelum reload
        setTimeout(() => {
            window.location.reload();
        }, 1000);
    };

    return (
        <Box className="destrak-container">
            <CustomBreadcrumbs breadcrumbItems={breadcrumbs} />

            <Card>
                <CardContent sx={{ width: '100%', overflow: 'hidden', p: 2 }}>
                    {/* Gunakan komponen DestrakToolbar */}
                    <DestrakToolbar
                        searchTerm={searchTerm}
                        onSearchChange={(e) => handleSearch(e.target.value)}
                        onResetFilter={handleResetFilter}
                        anchorEl={anchorEl}
                        onMenuClick={handleClick}
                        onMenuClose={handleClose}
                        open={open}
                        selectedItems={selectedItems}
                        userJabatan={userJabatan}
                        userRoleNames={userRoleNames}
                        updateStatusPengamatan={updateStatusPengamatan}
                        onStatusUpdate={handleStatusUpdate}
                        onReload={handleReload}
                        orders={orders}
                    />
                    
                    <TableContainer className="destrak-table-container">
                        <Table stickyHeader className="destrak-table">
                            <DestrakTableHeader 
                                selectedItems={selectedItems}
                                orders={filteredOrders}
                                handleSelectAll={handleSelectAll}
                            />
                            <DestrakTableBody 
                                orders={filteredOrders}
                                selectedItems={selectedItems}
                                handleSelectItem={handleSelectItem}
                                loadingOrders={loadingOrders}
                            />
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default Destrak;